/**
 * deviceDetect.ts — 「PC 画面か PWA(/mobile) か」の判定（2026-09-20）
 *
 * 以前は画面幅（960px 未満）だけで /mobile へ飛ばしていたため、PC でウィンドウを
 * 半分にする（LINE と並べる等）だけでスマホ画面に切り替わっていた。
 * 「PC で操作しているときはスマホ表示にならない」を最優先にし、
 *
 *   1. スマホ（UA が携帯端末）            → /mobile
 *   2. タッチ端末で幅が狭い（タブレット縦） → /mobile（従来どおり）
 *   3. それ以外（PC。タッチ無し）          → 幅に関係なく PC 画面
 *
 * とする。画面内のレイアウト調整（AppBar/Drawer、固定列など）は引き続き幅で行う。
 * スマホを横にしても幅は狭いので、そちらは幅が正しい。
 *
 * Chromium は navigator.userAgentData.mobile が使える。無い環境（Safari/Firefox）は
 * userAgent 文字列で判定する。iPad の Safari は UA が Mac と同じなので UA では
 * 拾えないが、maxTouchPoints > 1 なのでタッチ端末として 2 に落ちる。
 */

interface NavigatorLike {
  userAgent?: string;
  maxTouchPoints?: number;
  userAgentData?: { mobile?: boolean } | null;
}

/** UA が携帯端末（スマホ）か。タブレットは含めない */
export function isPhoneUserAgent(nav: NavigatorLike | undefined = typeof navigator !== 'undefined' ? navigator : undefined): boolean {
  if (!nav) return false;
  const uad = (nav as any).userAgentData;
  if (uad && typeof uad.mobile === 'boolean') return uad.mobile;
  const ua = String(nav.userAgent || '');
  if (/iPhone|iPod/.test(ua)) return true;
  if (/Android/.test(ua) && /Mobile/.test(ua)) return true; // Android タブレットは "Mobile" が付かない
  if (/Windows Phone|BlackBerry|Opera Mini/.test(ua)) return true;
  return false;
}

/** タッチ操作の端末か（iPad / Android タブレット / タッチ対応 PC も true になる） */
export function isTouchDevice(nav: NavigatorLike | undefined = typeof navigator !== 'undefined' ? navigator : undefined): boolean {
  if (!nav) return false;
  return (nav.maxTouchPoints ?? 0) > 1;
}

/**
 * PWA(/mobile) へ誘導すべきか。
 * @param isNarrow 画面幅が 960px 未満か（useMediaQuery(down('md'))）
 */
export function shouldUseMobileApp(
  isNarrow: boolean,
  nav: NavigatorLike | undefined = typeof navigator !== 'undefined' ? navigator : undefined,
): boolean {
  if (isPhoneUserAgent(nav)) return true;
  // タブレットは従来どおり幅で決める。PC（タッチ無し）は幅が狭くても PC 画面のまま
  return isNarrow && isTouchDevice(nav);
}
